// === Zod schemas: daily_check_sheet ===
// Tables: daily_check_sheet / daily_check_sheet_item / daily_check_sheet_image
//
// A sheet is answered against the three settings lists (Daily Check List, Pre
// Check, Post Check) but stores its own copy of every line it answered, so a
// printed sheet never changes when a preset is reworded or retired.

import { z } from 'zod';

export const CHECK_SHEET_PHASES = ['daily', 'pre', 'post'] as const;
export type CheckSheetPhase = (typeof CHECK_SHEET_PHASES)[number];

/** 1 = Yes, 0 = No, null = not answered. Null is a real state: an unanswered
 *  check is not a failed check, so it is never coerced to 0. */
const CheckedSchema = z.union([z.literal(0), z.literal(1)]).nullable();

export const DailyCheckSheetItemSchema = z.object({
  phase:         z.enum(CHECK_SHEET_PHASES),
  source_id:     z.number().int().positive().nullable().optional(),
  description:   z.string().min(1).max(500),
  checked:       CheckedSchema.optional(),
  notes:         z.string().max(2000).nullable().optional(),
  display_order: z.number().int().min(0).optional(),
});

export const DailyCheckSheetImageSchema = z.object({
  image_path:    z.string().min(1),
  item_label:    z.string().max(500).nullable().optional(),
  note:          z.string().max(500).nullable().optional(),
  display_order: z.number().int().min(0).optional(),
});

export const DailyCheckSheetCreateSchema = z.object({
  // The block trio every task table carries. block_id is what joins use,
  // block_name is what reports match on — a form must send both.
  block_type:     z.string().max(50).nullable().optional(),
  block_name:     z.string().max(300).nullable().optional(),
  block_id:       z.number().int().nullable().optional(),
  /** crop_types IDS, not names — same as every other task table. */
  crop:           z.string().max(120).nullable().optional(),
  crop_variety:   z.string().max(150).nullable().optional(),
  year:           z.string().max(4).nullable().optional(),
  week:           z.number().int().nullable().optional(),
  reference:      z.string().max(200).nullable().optional(),
  start_time:     z.string().nullable().optional(),
  finish_time:    z.string().nullable().optional(),
  assigned_user:  z.string().max(150).nullable().optional(),
  /** Comma-separated staff uuids from the labour picker. */
  labour_id:      z.string().nullable().optional(),
  /** Unnamed crew, IN ADDITION to the named staff. */
  staff_count:    z.number().int().min(0).nullable().optional(),
  task_status:    z.string().max(100).nullable().optional(),
  notes:          z.string().max(5000).nullable().optional(),
  sheet_complete: z.union([z.literal(0), z.literal(1)]).optional(),
  items:          z.array(DailyCheckSheetItemSchema).optional(),
  images:         z.array(DailyCheckSheetImageSchema).optional(),
});

export const DailyCheckSheetUpdateSchema = DailyCheckSheetCreateSchema;

export const DailyCheckSheetListQuerySchema = z.object({
  search:    z.string().optional(),
  status:    z.enum(['all', 'open', 'complete']).optional().default('all'),
  date_from: z.string().optional(),
  date_to:   z.string().optional(),
  page:      z.coerce.number().int().positive().optional().default(1),
  per_page:  z.coerce.number().int().positive().max(100).optional().default(25),
});

/** The print/report feed: same period filter, every sheet in full. */
export const DailyCheckSheetReportQuerySchema = z.object({
  date_from: z.string().optional(),
  date_to:   z.string().optional(),
  search:    z.string().optional(),
});

export type DailyCheckSheetItemInput = z.infer<typeof DailyCheckSheetItemSchema>;
export type DailyCheckSheetImageInput = z.infer<typeof DailyCheckSheetImageSchema>;
export type DailyCheckSheetCreate = z.infer<typeof DailyCheckSheetCreateSchema>;
export type DailyCheckSheetUpdate = z.infer<typeof DailyCheckSheetUpdateSchema>;
export type DailyCheckSheetListQuery = z.infer<typeof DailyCheckSheetListQuerySchema>;
